import type { CalendarEvent } from './types';

/**
 * Generates a random ID for events
 */
export const generateId = (): string => {
    return Math.random().toString(36).substring(2, 11);
};

/**
 * Formats a time string (HH:MM) to 12-hour format with AM/PM
 */
export const formatTime = (timeString: string): string => {
    const parts = timeString.split(':');
    const hourStr = parts[0] ?? '0';
    const minute = parts[1] ?? '00';
    const hour = parseInt(hourStr, 10);

    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minute} ${period}`;
};

/**
 * Calculates the duration between two time strings in minutes
 */
export const calculateDuration = (startTime: string, endTime: string): number => {
    const startParts = startTime.split(':').map(Number);
    const endParts = endTime.split(':').map(Number);
    const startHour = startParts[0] ?? 0;
    const startMinute = startParts[1] ?? 0;
    const endHour = endParts[0] ?? 0;
    const endMinute = endParts[1] ?? 0;

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    if (endMinutes < startMinutes) {
        return (24 * 60 - startMinutes) + endMinutes;
    }

    return endMinutes - startMinutes;
};

export const formatDateKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const addDays = (date: Date, days: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

export const getWeekStart = (date: Date): Date => {
    const start = new Date(date);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    start.setHours(0, 0, 0, 0);
    return start;
};

export const getWeekDates = (date: Date): Date[] => {
    const start = addDays(date, -1);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

export const isSameDay = (a: Date, b: Date): boolean => {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
};

export interface EventLayout {
    event: CalendarEvent;
    column: number;
    totalColumns: number;
}

export const timeToMinutes = (time: string): number => {
    const parts = time.split(':').map(Number);
    const hours = parts[0] ?? 0;
    const minutes = parts[1] ?? 0;
    return hours * 60 + minutes;
};

export const eventsOverlap = (a: CalendarEvent, b: CalendarEvent): boolean => {
    const aStart = timeToMinutes(a.startTime);
    const aEnd = timeToMinutes(a.endTime);
    const bStart = timeToMinutes(b.startTime);
    const bEnd = timeToMinutes(b.endTime);
    return aStart < bEnd && aEnd > bStart;
};

export const calculateEventLayouts = (events: CalendarEvent[]): EventLayout[] => {
    if (events.length === 0) return [];

    const sorted = [...events].sort((a, b) => {
        const aStart = timeToMinutes(a.startTime);
        const bStart = timeToMinutes(b.startTime);
        if (aStart !== bStart) return aStart - bStart;
        const aEnd = timeToMinutes(a.endTime);
        const bEnd = timeToMinutes(b.endTime);
        return bEnd - aEnd;
    });

    const layouts: Map<string, EventLayout> = new Map();
    const columns: CalendarEvent[][] = [];

    for (const event of sorted) {
        let placed = false;

        for (let col = 0; col < columns.length; col++) {
            const columnEvents = columns[col];
            if (!columnEvents) continue;
            const hasOverlap = columnEvents.some(e => eventsOverlap(e, event));

            if (!hasOverlap) {
                columnEvents.push(event);
                layouts.set(event.id, { event, column: col, totalColumns: 0 });
                placed = true;
                break;
            }
        }

        if (!placed) {
            columns.push([event]);
            layouts.set(event.id, {
                event,
                column: columns.length - 1,
                totalColumns: 0,
            });
        }
    }

    for (const layout of layouts.values()) {
        const overlappingEvents = sorted.filter(e =>
            eventsOverlap(e, layout.event)
        );
        const overlappingColumns = new Set<number>();

        for (const overlapping of overlappingEvents) {
            const overlappingLayout = layouts.get(overlapping.id);
            if (overlappingLayout) {
                overlappingColumns.add(overlappingLayout.column);
            }
        }

        layout.totalColumns = Math.max(overlappingColumns.size, 1);
    }

    return Array.from(layouts.values());
};
